import InputField from "components/FormFields/InputField";
import { PayoutModalPropsType } from "components/PayoutManagement/types";
import { addPayoutMethod, updatePayoutMethod } from "constants/apiUrls";
import { PayoutMethodEnum } from "enum/common";
import { useFormik } from "formik";
import { getCountryCallingCode, isValidNumber } from "libphonenumber-js";
import { useAppSelector } from "hooks/useRedux";
import { http } from "middleware/apiMiddleware";
import { useState } from "react";
import toast from "react-hot-toast";
import { RootState } from "store";
import * as Yup from "yup";
import RadioField from "components/FormFields/RadioField";

interface IZelleFormProps {
  data: PayoutModalPropsType;
  afterSubmit: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

enum SubmissionTypeEnum {
  EMAIL = "email",
  PHONE = "phoneNumber",
}
const ZelleForm: React.FC<IZelleFormProps> = ({
  data,
  afterSubmit,
  setIsSubmitting,
}) => {
  const defaultSubmissionType = data?.data?.phoneNumber
    ? SubmissionTypeEnum.PHONE
    : SubmissionTypeEnum.EMAIL;
  const [submissionType, setSubmissionType] =
    useState<SubmissionTypeEnum | null>(defaultSubmissionType);
  const selectedBusiness = useAppSelector(
    (state: RootState) => state.businesses.business
  );
  const countryCode: any = selectedBusiness?.address?.countryShortCode || "US";
  const phoneCountryExtension = "+1" || getCountryCallingCode(countryCode);

  const validationSchema = (submissionType: SubmissionTypeEnum | null) =>
    Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .when([], {
          is: () => submissionType === SubmissionTypeEnum.EMAIL,
          then: (schema) => schema.required("Email is required"),
          otherwise: (schema) => schema.notRequired(),
        }),

      phoneNumber: Yup.string()
        .test("is-valid-phone", "Invalid phone number", (value) => {
          if (!value) return true; // Skip validation if empty (not required)
          return isValidNumber(`${phoneCountryExtension}${value}`);
        })
        .when([], {
          is: () => submissionType === SubmissionTypeEnum.PHONE,
          then: (schema) => schema.required("Phone number is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
    });

  const isEdit = data?.type === "edit";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: data?.data?.email,
      phoneNumber: data?.data?.phoneNumber,
      phoneCountryName: selectedBusiness?.address?.country,
      phoneCountryCode: selectedBusiness?.address?.countryShortCode,
      phoneCountryExtension: phoneCountryExtension,
    },
    validationSchema: validationSchema(submissionType),
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const payload =
          submissionType === "email"
            ? {
                email: values?.email,
              }
            : {
                phoneCountryExtension: phoneCountryExtension,
                phoneCountryName: selectedBusiness?.address?.country,
                phoneCountryCode: selectedBusiness?.address?.countryShortCode,
                phoneNumber: values?.phoneNumber,
                email: "",
              };
        if (isEdit) {
          await http.patch(
            updatePayoutMethod(
              PayoutMethodEnum.ZELLE,
              data?.data?.uuid || "",
              selectedBusiness?.uuid || ""
            ),
            payload
          );
        } else {
          await http.post(
            addPayoutMethod(
              PayoutMethodEnum.ZELLE,
              selectedBusiness?.uuid || ""
            ),
            payload
          );
        }
        afterSubmit();
      } catch (error) {
        toast("Unexpected error occurred while adding/updating payout method");
      } finally {
        setIsSubmitting(false);
      }
    },
  });
  const { values, setFieldValue, errors, handleSubmit, touched, resetForm } =
    formik;

  return (
    <div>
      <form onSubmit={handleSubmit} id={PayoutMethodEnum.ZELLE}>
        <RadioField
          containerClass={"flex flex-row"}
          value={submissionType || ""}
          options={[
            { label: "Email Address", value: SubmissionTypeEnum.EMAIL },
            { label: "Phone Number", value: SubmissionTypeEnum.PHONE },
          ]}
          name={"zelleForm"}
          onChange={(value) => {
            resetForm();
            setSubmissionType(value?.value);
            setFieldValue("email", "");
            setFieldValue("phoneNumber", "");
          }}
        />
        {submissionType === SubmissionTypeEnum.EMAIL ? (
          <InputField
            id="email"
            placeholder="Enter Email Address"
            onChange={(e) => {
              setFieldValue("email", e.target.value);
            }}
            value={values?.email}
            error={touched.email && errors.email}
          />
        ) : (
          <InputField
            id="phonenNumber"
            type={"number"}
            placeholder="Enter Phone Number"
            onChange={(e) => {
              setFieldValue("phoneNumber", e.target.value);
            }}
            value={values?.phoneNumber}
            error={touched.phoneNumber && errors.phoneNumber}
          />
        )}
      </form>
    </div>
  );
};

export default ZelleForm;
